import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Button from '../../atoms/Button/Button';
import HighlightedText from '../../atoms/HighlightedText/HighlightedText';
import Tag from '../../atoms/Tag/Tag';
import LabelWithIconAndColor from '../LabelWithIconAndColor/LabelWithIconAndColor';

// Utils
import cn from '../../../utils/cn';

// Styles
import styles from './LiveSearchResultCard.module.css';

/**
 * @param {object} props
 * @param {LiveSearchResultCardTextProps} props.side
 * @param {string[]} props.highlightTerms
 * @param {boolean} [props.highlightText]
 * @param {string} [props.className]
 * @param {string} [props.labelColor]
 * @returns {JSX.Element}
 */
function LiveSearchTextSide({
  side,
  highlightTerms,
  highlightText = false,
  className,
  labelColor,
}) {
  const label = highlightText
    ? <HighlightedText text={side.text} terms={highlightTerms} />
    : side.text;

  return (
    <LabelWithIconAndColor
      label={label}
      icon={side.icon}
      iconColor={side.iconColor}
      iconPosition="left"
      className={className}
      labelColor={labelColor}
    />
  );
}

/**
 * Multi-cell row: equal columns, label above + value pastille.
 * @param {object} props
 * @param {LiveSearchResultCardRowProps} props.row
 * @param {string[]} props.highlightTerms
 * @returns {JSX.Element}
 */
function LiveSearchPastilleRow({ row, highlightTerms }) {
  return (
    <div
      className={cn([styles.row, styles['row-pastilles']])}
      style={/** @type {import('react').CSSProperties} */ ({
        '--cols': row.values.length,
      })}
    >
      {row.values.map((cell) => (
        <div
          key={`${cell.label.text}-${cell.label.icon}-${cell.value.text}-${cell.value.icon}`}
          className={styles.cell}
        >
          {cell.label.text !== '' ? (
            <span className={styles['cell-label']}>{cell.label.text}</span>
          ) : null}
          <Tag theme="primary" className={styles['cell-pastille']}>
            <HighlightedText text={cell.value.text} terms={highlightTerms} />
          </Tag>
        </div>
      ))}
    </div>
  );
}

/**
 * Single-cell row: plain grey line (no pastille). Label omitted when empty (API-driven).
 * @param {object} props
 * @param {LiveSearchResultCardRowProps} props.row
 * @param {string[]} props.highlightTerms
 * @returns {JSX.Element}
 */
function LiveSearchPlainRow({ row, highlightTerms }) {
  const cell = row.values[0];

  return (
    <div className={cn([styles.row, styles['row-plain']])}>
      {cell.label.text !== '' ? (
        <LiveSearchTextSide
          side={cell.label}
          highlightTerms={highlightTerms}
          className={styles['plain-label']}
          labelColor="var(--neutral-500)"
        />
      ) : null}
      <LiveSearchTextSide
        side={cell.value}
        highlightTerms={highlightTerms}
        highlightText
        className={styles['plain-value']}
        labelColor="var(--neutral-500)"
      />
    </div>
  );
}

/**
 * LiveSearchResultCard - Displays a live search result with title, optional subtitle,
 * rows (pastilles when multi-cell, plain grey line when single-cell) and expandable actions.
 * @param {object} props
 * @param {string} props.title - Main title
 * @param {string | null} [props.subTitle] - Optional secondary line under the title (no label)
 * @param {boolean} [props.isActive=true] - Whether the entity is active
 * @param {LiveSearchResultCardRowProps[]} [props.rows=[]] - Display rows
 * @param {Array<{ label: string, icon: string }>} [props.actions=[]] - Action buttons
 * @param {string[]} [props.highlightTerms=[]] - Query terms to highlight in title, subtitle and values
 * @param {(actionIndex: number) => void} [props.onActionClick] - Action click handler
 * @param {boolean} [props.disabled] - Disable action buttons
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} LiveSearchResultCard component
 */
function LiveSearchResultCard({
  title,
  subTitle = null,
  isActive = true,
  rows = [],
  actions = [],
  highlightTerms = [],
  onActionClick,
  disabled = false,
  className,
}) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(/** @type {number | null} */ (null));

  /**
   * @param {React.MouseEvent<HTMLDivElement> | React.FocusEvent<HTMLDivElement>} event
   */
  const setActiveIndexFromEvent = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('button');
    if (!button) return;
    const index = [...event.currentTarget.children].indexOf(button);
    if (index >= 0) setActiveIndex(index);
  };

  return (
    <article className={cn([styles.card, !isActive && styles.inactive, className])}>
      <header className={styles.header}>
        <div className={styles['title-block']}>
          <h3 className={styles.title}>
            <HighlightedText text={title} terms={highlightTerms} />
          </h3>
          {/* TODO surnom: nickname line under the title (waiting for API field) */}
          {subTitle != null && subTitle !== '' ? (
            <p className={styles.subtitle}>
              <HighlightedText text={subTitle} terms={highlightTerms} />
            </p>
          ) : null}
        </div>
        {!isActive && (
          <Tag theme="neutral">{t('form.liveSearch.inactive')}</Tag>
        )}
      </header>

      {rows.length > 0 && (
        <div className={styles.content}>
          {rows.map((row) => {
            const key = row.values.map((cell) => `${cell.label.text}:${cell.value.text}`).join('|');
            const isPastilleRow = row.values.length > 1;
            if (row.values.length === 0) return null;

            return isPastilleRow ? (
              <LiveSearchPastilleRow key={key} row={row} highlightTerms={highlightTerms} />
            ) : (
              <LiveSearchPlainRow key={key} row={row} highlightTerms={highlightTerms} />
            );
          })}
        </div>
      )}

      {actions.length > 0 && (
        <div
          className={styles.actions}
          onMouseOver={setActiveIndexFromEvent}
          onMouseLeave={() => setActiveIndex(null)}
          onFocus={setActiveIndexFromEvent}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setActiveIndex(null);
            }
          }}
        >
          {actions.map((action, actionIndex) => {
            const isPrimary = actionIndex === 0;
            const isExpanded = actionIndex === (activeIndex ?? 0);
            return (
              <Button
                key={`${action.label}-${action.icon}`}
                color={isPrimary ? 'primary' : 'primary100'}
                size="small"
                icon={action.icon}
                ariaLabel={action.label}
                className={cn([
                  styles.action,
                  isPrimary && styles['action-primary'],
                  isExpanded && styles['action-expanded'],
                ])}
                onClick={() => onActionClick?.(actionIndex)}
                disabled={disabled}
              >
                <span className={styles['action-label']}>{action.label}</span>
              </Button>
            );
          })}
        </div>
      )}
    </article>
  );
}

export default LiveSearchResultCard;
